/**
 * Notification Types
 *
 * Type definitions for the notifications feature.
 */

// Re-export WebSocket event types
export type { NotificationPayload, NotificationCountPayload } from '@/lib/websocket/events';
import { formatTenantDate } from '@/lib/locale-format';

// =============================================================================
// ENUMS & CONSTANTS
// =============================================================================

export type NotificationType = 'EMAIL' | 'SMS' | 'IN_APP' | 'PUSH';

export type NotificationCategory =
  | 'AUTH'
  | 'ACCOUNT'
  | 'SECURITY'
  | 'CRM'
  | 'TENANT'
  | 'HRMS'
  | 'PAYROLL'
  | 'LEAVE'
  | 'REIMBURSEMENT'
  | 'ATTENDANCE'
  | 'ASSET'
  | 'COMPLIANCE'
  | 'SYSTEM'
  | 'ANNOUNCEMENT';

export type NotificationPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

// Labels for display
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  AUTH: 'Authentication',
  ACCOUNT: 'Account',
  SECURITY: 'Security',
  CRM: 'CRM',
  TENANT: 'Tenant',
  HRMS: 'HR Management',
  PAYROLL: 'Payroll',
  LEAVE: 'Leave',
  REIMBURSEMENT: 'Reimbursement',
  ATTENDANCE: 'Attendance',
  ASSET: 'Asset',
  COMPLIANCE: 'Compliance',
  SYSTEM: 'System',
  ANNOUNCEMENT: 'Announcement',
};

export const NOTIFICATION_CATEGORY_COLORS: Record<NotificationCategory, string> = {
  AUTH: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  ACCOUNT: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  SECURITY: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  CRM: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  TENANT: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
  HRMS: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  PAYROLL: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  LEAVE: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  REIMBURSEMENT: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  ATTENDANCE: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
  ASSET: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
  COMPLIANCE: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300',
  SYSTEM: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  ANNOUNCEMENT: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
};

// =============================================================================
// NOTIFICATION TYPES
// =============================================================================

export interface Notification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  body: string;
  data: Record<string, unknown>;
  timestamp: string;
  read: boolean;
  priority?: NotificationPriority;
  actionUrl?: string;
  actionLabel?: string;
  imageUrl?: string;
  expiresAt?: string;
}

export interface NotificationPreferences {
  id?: string | null;
  email: boolean;
  push: boolean;
  inApp: boolean;
  sms: boolean;
  mutedUntil?: string | null;
  categories: {
    [key in NotificationCategory]?: {
      enabled: boolean;
      email?: boolean;
      inApp?: boolean;
      sms?: boolean;
      push?: boolean;
    };
  };
  quietHours?: {
    enabled: boolean;
    start: string; // HH:mm format
    end: string;
  };
}

// =============================================================================
// API TYPES
// =============================================================================

export interface NotificationFilters {
  category?: NotificationCategory;
  read?: boolean;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export interface NotificationStats {
  total: number;
  unread: number;
  byCategory: Record<NotificationCategory, number>;
  recentCount: number; // Last 24 hours
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get relative time string (e.g., "2 minutes ago")
 */
export function getRelativeTime(timestamp: string | undefined | null): string {
  if (!timestamp) return '';
  const now = new Date();
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return formatTenantDate(date);
}

/**
 * Group notifications by date
 */
export function groupNotificationsByDate(
  notifications: Notification[],
): Map<string, Notification[]> {
  const groups = new Map<string, Notification[]>();

  notifications.forEach((notification) => {
    const date = notification.timestamp ? new Date(notification.timestamp) : new Date();
    if (isNaN(date.getTime())) return; // Skip unparseable entries
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let key: string;
    if (date.toDateString() === today.toDateString()) {
      key = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = 'Yesterday';
    } else {
      key = formatTenantDate(date);
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)?.push(notification);
  });

  return groups;
}

/**
 * Get icon name for notification category
 */
export function getCategoryIcon(category: NotificationCategory): string {
  const icons: Record<NotificationCategory, string> = {
    AUTH: 'key',
    ACCOUNT: 'user',
    SECURITY: 'shield',
    CRM: 'users',
    TENANT: 'building',
    HRMS: 'briefcase',
    PAYROLL: 'banknote',
    LEAVE: 'calendar',
    REIMBURSEMENT: 'receipt',
    ATTENDANCE: 'clock',
    ASSET: 'package',
    COMPLIANCE: 'file-check',
    SYSTEM: 'settings',
    ANNOUNCEMENT: 'megaphone',
  };
  return icons[category] || 'bell';
}
